import React from 'react';
import { CONFIG } from '@hero/core';
import { ItemPacking } from '@hero/schema';
import { formatVehicleText } from '@hero/web-utils';
import { IconProps, PackingIconDynamic } from '@hero/web-icons';
import { SelectAuto, SelectAutoOption, Counter } from '@hero/web-components';
import { useTranslation } from 'react-i18next';
import { cn } from '@hero/web-lib';

interface OrderPackingTypeSelectProps {
  packing: ItemPacking | undefined;
  counter?: number;
  showCounter?: boolean;
  onPackingChange: (value: ItemPacking) => void;
  onCounterChange: (value: number) => void;
  /** Optional placeholder text */
  placeholder?: string;
  /** Optional className */
  className?: string;
  /** Optional icon props */
  iconProps?: IconProps;
  /** Optional disabled state */
  disabled?: boolean;
  /** Optional maximum quantity limit */
  maxCounter?: number;
}

/**
 * OrderPackingTypeSelect - A component for selecting packing type with optional counter.
 * Includes the select field with packing icons and labels, plus an optional counter input.
 *
 * @example
 * ```tsx
 * <OrderPackingTypeSelect
 *   itemPacking={selectedPacking}
 *   counter={quantity}
 *   showCounter={true}
 *   onPackingChange={(packing) => setSelectedPacking(packing)}
 *   onCounterChange={(count) => setQuantity(count)}
 *   placeholder="Select packing type"
 * />
 * ```
 */
export function OrderPackingTypeSelect({
  packing: itemPacking,
  counter = 0,
  showCounter = false,
  onPackingChange,
  onCounterChange,
  placeholder,
  className,
  iconProps,
  disabled = false,
  maxCounter: maxQuantity,
}: OrderPackingTypeSelectProps) {
  const { t, i18n } = useTranslation(['m-order']);
  const rtl = i18n.language === 'ar';

  const packingOptions = CONFIG.order.ordersLoadConfig.supportedPackingOptions;

  // See `OrderItemTypeSelect` — `formatVehicleText` is an English-only
  // prettifier, so it is the fallback rather than the label.
  const options: SelectAutoOption[] = packingOptions.map(packing => ({
    value: packing,
    label: t(`m-order:packing_types.${packing}`, formatVehicleText(packing)),
    icon: (
      <PackingIconDynamic packing={packing} size={'medium'} variant={'primary'} {...iconProps} />
    ),
  }));

  return (
    <div className={cn('flex items-center gap-3', rtl && 'flex-row-reverse', className)}>
      <div className="flex-1 min-w-0">
        <SelectAuto
          options={options}
          value={itemPacking}
          onChange={val => onPackingChange(val as ItemPacking)}
          placeholder={placeholder || t('m-order:load_selects.packing_placeholder')}
          disabled={disabled}
          triggerClassName="text-[16px] font-[600] leading-[24px]"
        />
      </div>
      {showCounter && (
        <div className="flex-shrink-0">
          <Counter
            value={counter}
            onChange={onCounterChange}
            min={0}
            max={maxQuantity}
            disabled={disabled}
          />
        </div>
      )}
    </div>
  );
}
